import { useMemo } from 'react'
import { useClientConfig } from '@/entities/client-config/model'
import { groupPartnerOrders } from '@/entities/order/lib'
import { useRawOrdersQuery } from '@/entities/order/model'
import { useStateConfigQuery } from '@/entities/state/model'

/**
 * Get all orders that are in the same group as the given order
 *
 * This is used for ensuring all orders in a group are updated together
 * from components like OrderDetailsModal
 *
 * @param orderId - The ID of the order to find group members for
 * @returns Array of order IDs that are in the same group
 */
export function useOrderGroupMembers(orderId: string): string[] {
  const { data: orders } = useRawOrdersQuery()
  const { config: clientConfig } = useClientConfig()
  const { data: stateConfig } = useStateConfigQuery()
  const groupField = clientConfig?.service?.groupField

  return useMemo(() => {
    if (!stateConfig?.features?.orderGrouping?.enabled || !orders?.length || !orderId) {
      return [orderId]
    }

    const currentOrder = orders.find(order => order.orderId === orderId)
    if (!currentOrder) {
      return [orderId]
    }

    try {
      const groupedOrders = groupPartnerOrders(orders, {
        maxGroupSize: Number.MAX_SAFE_INTEGER,
        groupField,
      })

      for (const dashboardOrder of groupedOrders) {
        if (dashboardOrder.type === 'group') {
          if (dashboardOrder.data.orders.some(order => order.orderId === orderId)) {
            return dashboardOrder.data.orders.map(order => order.orderId)
          }
        } else if (dashboardOrder.type === 'single' && dashboardOrder.id === orderId) {
          return [orderId]
        }
      }
    } catch (error: any) {
      console.error('Error grouping partner orders:', error)
      // If groupPartnerOrders throws, fall back to single order
      return [orderId]
    }

    return [orderId]
  }, [orderId, orders, stateConfig?.features?.orderGrouping?.enabled, groupField])
}
